import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { docEntities } from '../constants/common';
import deleteFlatAttachment from '../requests/deleteFlatAttachment';
import deleteHouseAttachment from '../requests/deleteHouseAttachment';
import addAttachmentToFlat from '../requests/addAttachmentToFlat';
import addAttachmentToHouse from '../requests/addAttachmentToHouse';
import changeFlatAttachment from '../requests/changeFlatAttachment';
import changeHouseAttachment from '../requests/changeHouseAttachment';
import { fieldNames } from '../components/ui/modals/DocFormModal/constants';

const deleteFns = {
  [docEntities.room]: deleteFlatAttachment,
  [docEntities.house]: deleteHouseAttachment,
};

export const useDeleteDocMutation = (data) => {
  const { entity, params } = data;

  const queryClient = useQueryClient();

  const deleteDoc = useMutation(
    ({ id, attachId }) => deleteFns[entity]?.(id, attachId),
    {
      onSuccess: (_data, variables) => {
        queryClient.setQueryData(params?.keys, (oldDocs) =>
          oldDocs.filter((doc) => doc.id !== variables.attachId)
        );
      },
    }
  );

  const mutate = useCallback(
    (attachment) =>
      deleteDoc.mutate({ id: params?.id, attachId: attachment.id }),
    [params?.id]
  );

  return { mutate, isLoading: deleteDoc.isLoading };
};

const addFns = {
  [docEntities.room]: addAttachmentToFlat,
  [docEntities.house]: addAttachmentToHouse,
};

export const useAddDocMutation = (data) => {
  const { entity, params, typeId } = data;

  const queryClient = useQueryClient();

  const addDoc = useMutation(
    async ({ id, files, data }) => {
      const fn = addFns[entity];

      if (!fn) return;

      if (files?.length === 1) {
        return await fn(id, files?.[0], data, typeId);
      }

      return Promise.all(
        files.map((file) => {
          const parts = file?.name?.split('.');
          const name = parts?.slice(0, -1).join('.');

          return fn(id, file, { ...data, name }, typeId);
        })
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(params?.keys);
      },
    }
  );

  const mutate = useCallback(
    ({ files, data }) => addDoc.mutateAsync({ id: params?.id, files, data }),
    [params?.id]
  );

  return { mutate, isLoading: addDoc.isLoading };
};

const changeFns = {
  [docEntities.room]: changeFlatAttachment,
  [docEntities.house]: changeHouseAttachment,
};

export const useChangeDocMutation = (data) => {
  const { entity, params } = data;

  const queryClient = useQueryClient();

  const changeDoc = useMutation(
    ({ id, data, attachId }) => changeFns[entity]?.({ id, data, attachId }),
    {
      onSuccess: (_data, variables) => {
        queryClient.setQueryData(params?.keys, (oldDocs) =>
          oldDocs.map((doc) => {
            if (doc.id === variables.attachId) {
              const { data } = variables;
              return {
                ...doc,
                name: data[fieldNames.name],
                availableForClient: data[fieldNames.availableToClient],
                description: data[fieldNames.description],
                fromDate: data[fieldNames.fromDate],
                toDate: data[fieldNames.toDate],
              };
            }

            return doc;
          })
        );
      },
    }
  );

  const mutate = useCallback(
    ({ data, attachId }) =>
      changeDoc.mutateAsync({ id: params?.id, data, attachId }),
    [params?.id]
  );

  return { mutate, isLoading: changeDoc.isLoading };
};
